import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import * as yup from 'yup';

import AdminFormPage from 'components/admin/shared/AdminFormPage';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CircularProgress from 'components/shared/CircularProgress';
import Foldout from 'components/shared/Foldout';
import { FormContainer as Form } from 'components/shared/form/Form';
import FormHeader from 'components/shared/form/FormHeader';
import FormSection from 'components/shared/form/FormSection';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import SectionHeader from 'components/shared/form/SectionHeader';
import {
  CONFIGURATION_TYPE_OPTS,
  DELIMITER_OPTS,
  IMPORTED_PATIENT,
  INTEGRATION,
  PATIENT_MAPPINGS,
  PLATFORM_OPTS,
} from 'constants/importConfigurations';
import { useProfile } from 'context/profile';
import { getFieldError, useAdminForm } from 'hooks/datasource';
import useInfiniteOptions from 'hooks/useInfiniteOptions';
import { Classification, ClassificationOptions } from 'models/flexible/Classification';
import { Client } from 'models/flexible/Client';
import { GroupOptions } from 'models/flexible/Group';
import { GroupTypeOptions } from 'models/flexible/GroupType';
import { GroupType, GroupTypeApiName } from 'models/flexible/GroupType';
import { ImportConfiguration, ImportConfigurationOptions } from 'models/flexible/ImportConfiguration';
import { ClassificationType, GroupTypeType, Mapping } from 'models/flexible/ImportConfiguration';
import { Location, LocationOptions } from 'models/flexible/Location';
import { PERMISSIONS } from 'models/flexible/Profile';
import { adminIndexClassifications } from 'services/api/admin/classifications';
import { adminIndexClients } from 'services/api/admin/clients';
import { indexGroupTypes } from 'services/api/admin/groupTypes';
import {
  adminCreateImportConfiguration,
  adminImportConfigurationsQueryKeys,
  adminShowImportConfiguration,
  adminUpdateImportConfiguration,
} from 'services/api/admin/importConfigurations';
import { adminIndexLocations } from 'services/api/admin/locations';
import type { GroupTypeFields } from 'services/api/fields';
import { colors } from 'styles/theme/colors';
import DetailsIcon from 'svg/DetailsIcon';
import FormIndent from 'svg/FormIndent';
import RightArrowIcon from 'svg/RightArrowIcon';
import TrashIcon from 'svg/TrashIcon';

import LookupsSlideout, { LookupName } from './LookupsSlideout';

// ---------------------------------------------------------------------------
// Constants + helpers
// ---------------------------------------------------------------------------

const getId = (o: any) => o!.id as string;
const getName = (o: any) => o!.name as string;
const getApiName = (o: any) => o!.apiName as string;
const getDisplayName = (o: any) => o!.displayName as string;

const CLASSIFICATION_TYPE_OPTIONS = [
  new ClassificationType({ name: 'Episode Type', apiName: 'episode' }),
  new ClassificationType({ name: 'Plan Type', apiName: 'plan_type' }),
];

const GROUP_TYPE_TYPE_OPTIONS = [
  new GroupTypeType({ displayName: 'Care Option', apiName: 'provider' }),
  new GroupTypeType({ displayName: 'Manager', apiName: 'manager' }),
];

const defaultConfig = {
  dateFormat: '%m/%d/%Y',
  delimiter: DELIMITER_OPTS[0],
  platform: null as { label: string; value: string } | null,
  properties: {},
  lookups: { episodes: {}, plan_types: {}, groups: {}, group_types: {}, locations: {} },
  mappings: {
    classifications: [] as Mapping<ClassificationType, ClassificationOptions>[],
    groups: [] as Mapping<GroupTypeOptions, GroupOptions>[],
    groupTypes: [] as Mapping<GroupTypeType, null>[],
    locations: [{ columnName: '', type: null, defaultValue: null }] as Mapping<null, LocationOptions>[],
    transferredFrom: { columnName: '', type: null, defaultValue: null } as Mapping<GroupTypeOptions, GroupOptions>,
  },
};

const classificationMappingSchema = yup
  .array()
  .of(
    yup
      .object({
        columnName: yup.string().nullable(),
        defaultValue: yup.object({ id: yup.string(), name: yup.string() }).nullable(),
        type: yup.object({ apiName: yup.string(), name: yup.string() }).required('Classification Type is required'),
      })
      .test(
        'columnNameOrDefaultValue',
        'Either Client Header or Default Value is required',
        (value) => !!(value.columnName || value.defaultValue)
      )
  )
  .test('onlyOneMappingPerClassificationType', 'Only one mapping per Classification Type can exist', (value) => {
    if (!value) return true;
    const apiNames = value.map((item) => item.type?.apiName);
    return apiNames.length === new Set(apiNames).size;
  });

const groupMappingSchema = yup
  .array()
  .of(
    yup.object({
      type: yup.object({ apiName: yup.string(), name: yup.string() }).required('Group Type is required'),
    })
  )
  .test('onlyOneMappingPerGroupType', 'Only one mapping per Group Type can exist', (value) => {
    if (!value) return true;
    const apiNames = value.map((item) => item.type?.apiName);
    return apiNames.length === new Set(apiNames).size;
  });

const groupTypeMappingSchema = yup
  .array()
  .of(
    yup
      .object({
        columnName: yup.string().nullable(),
        type: yup.object({ apiName: yup.string(), name: yup.string() }).required('Category is required'),
      })
      .test('columnNameOrCategory', 'Client Header is required', (value) => !!value.columnName)
  )
  .test('onlyOneMappingPerCategory', 'Only one mapping per Category can exist', (value) => {
    if (!value) return true;
    const apiNames = value.map((item) => item.type?.apiName);
    return apiNames.length === new Set(apiNames).size;
  });

const locationMappingSchema = yup.array().of(
  yup.object({
    columnName: yup.string().nullable(),
    defaultValue: yup.object({ id: yup.string(), name: yup.string() }).nullable(),
  })
);

const validationSchema = yup.object({
  client: yup
    .object({ id: yup.string(), name: yup.string() })
    .nullable()
    .test('client', 'Client is required', (obj) => Boolean(obj && obj.id)),
  type: yup
    .object({ label: yup.string(), value: yup.string() })
    .nullable()
    .test('type', 'Configuration Type is required', (obj) => Boolean(obj && obj.value)),
  config: yup.object().when('type', {
    is: (t: any) => t?.value !== INTEGRATION,
    then: (schema) =>
      schema
        .shape({
          dateFormat: yup.string().required('Date Format is required'),
          delimiter: yup
            .object({ label: yup.string(), value: yup.string() })
            .nullable()
            .test('delimiter', 'Delimiter is required', (obj) => Boolean(obj && obj.value)),
          mappings: yup.object({
            dateOfBirth: yup.string().required('Date of Birth is required'),
            externalId: yup.string().required('Patient ID is required'),
            name: yup.string().required('Patient Name is required'),
            sex: yup.string().required('Gender is required'),
            classifications: classificationMappingSchema,
            groups: groupMappingSchema,
            groupTypes: groupTypeMappingSchema,
            locations: locationMappingSchema,
          }),
        })
        .nullable(),
    otherwise: (schema) =>
      schema
        .shape({
          platform: yup
            .object({ label: yup.string(), value: yup.string() })
            .nullable()
            .test('platform', 'System is required', (obj) => Boolean(obj && obj.value)),
          mappings: yup.object({
            classifications: classificationMappingSchema,
            groupTypes: groupTypeMappingSchema,
          }),
        })
        .nullable(),
  }),
});

// ---------------------------------------------------------------------------
// Mapping sub-sections
// ---------------------------------------------------------------------------

function ClassificationMappings({
  values,
  errors,
  setValue,
  disabled,
  onOpenSlideout,
}: {
  values: any;
  errors: any;
  setValue: (key: string, value: any) => void;
  disabled: boolean;
  onOpenSlideout: (name: LookupName) => void;
}) {
  const classifications = values.config.mappings.classifications ?? [];
  const [focusedType, setFocusedType] = useState<string | null>(classifications[0]?.type?.apiName ?? null);

  const availableTypeOptions = CLASSIFICATION_TYPE_OPTIONS.filter(
    (opt) => !classifications.some((c: any) => c.type?.apiName === opt.apiName)
  );

  const classificationOptions = useInfiniteOptions({
    queryKey: ['adminClassifications', 'importConfig', focusedType, values.client?.id],
    fetchFn: adminIndexClassifications,
    params: { client: values.client?.id, type: focusedType ?? '', sortBy: 'name asc' },
  });

  const addMapping = () =>
    setValue('config.mappings.classifications', [
      ...classifications,
      { defaultValue: null, type: null, columnName: '' },
    ]);
  const removeMapping = (i: number) =>
    setValue(
      'config.mappings.classifications',
      classifications.filter((_: any, idx: number) => idx !== i)
    );

  return (
    <>
      <SectionHeader>Classification Mappings</SectionHeader>
      {classifications.map((classification: any, index: number) => {
        const { type } = classification;
        const selectedType = CLASSIFICATION_TYPE_OPTIONS.find((o) => o.apiName === type?.apiName) ?? null;
        const itemError = getFieldError(errors, 'config', 'mappings', 'classifications', index);
        return (
          <MappingRowWithSubRow key={`${type?.apiName}-${index}`}>
            <MappingRow>
              <StyledInputGroup title='Client Header (optional)'>
                <Input
                  value={classification.columnName ?? ''}
                  onChange={(e) => setValue(`config.mappings.classifications[${index}].columnName`, e.target.value)}
                  disabled={disabled || !type}
                />
              </StyledInputGroup>
              <IconContainer>
                <RightArrowIcon />
              </IconContainer>
              <StyledInputGroup title='Classification Type'>
                <Select<ClassificationType>
                  options={availableTypeOptions}
                  value={selectedType}
                  onChange={(v) => setValue(`config.mappings.classifications[${index}].type`, v)}
                  getOptionValue={getApiName}
                  getOptionLabel={getName}
                  placeholder='Classification'
                  disabled={disabled}
                />
              </StyledInputGroup>
              {selectedType && (
                <IconContainer width='36px'>
                  <StyledDetailsIcon onClick={() => onOpenSlideout(`${selectedType.apiName}s` as LookupName)} />
                </IconContainer>
              )}
              <IconContainer width='36px'>
                {!disabled && <StyledTrashIcon onClick={() => removeMapping(index)} />}
              </IconContainer>
            </MappingRow>
            <MappingRow>
              <StyledFormIndent />
              <InputGroup title='Default Value (optional)' error={itemError}>
                <Select<Classification>
                  {...classificationOptions}
                  value={classification.defaultValue ?? null}
                  onChange={(v) => setValue(`config.mappings.classifications[${index}].defaultValue`, v)}
                  getOptionValue={getId}
                  getOptionLabel={getName}
                  onFocus={() => setFocusedType(classifications[index].type?.apiName ?? null)}
                  disabled={disabled || !type}
                  isClearable
                />
              </InputGroup>
            </MappingRow>
          </MappingRowWithSubRow>
        );
      })}
      {!disabled && availableTypeOptions.length > 0 && (
        <Link onClick={addMapping}>Add {classifications.length > 0 ? 'another' : 'a'} classification mapping</Link>
      )}
    </>
  );
}

function GroupTypeMappings({
  values,
  errors,
  setValue,
  disabled,
  onOpenSlideout,
}: {
  values: any;
  errors: any;
  setValue: (key: string, value: any) => void;
  disabled: boolean;
  onOpenSlideout: (name: LookupName) => void;
}) {
  const groupTypes = values.config.mappings.groupTypes ?? [];
  const availableOptions = GROUP_TYPE_TYPE_OPTIONS.filter(
    (opt) => !groupTypes.some((gt: any) => gt.type?.apiName.endsWith(opt.apiName))
  );

  const addMapping = () =>
    setValue('config.mappings.groupTypes', [...groupTypes, { defaultValue: null, type: null, columnName: '' }]);
  const removeMapping = (i: number) =>
    setValue(
      'config.mappings.groupTypes',
      groupTypes.filter((_: any, idx: number) => idx !== i)
    );

  return (
    <>
      <SectionHeader>Group Type Mappings</SectionHeader>
      {groupTypes.map((group: any, index: number) => {
        const { type } = group;
        const selectedType = GROUP_TYPE_TYPE_OPTIONS.find((o) => o.apiName === type?.apiName) ?? null;
        const itemError = getFieldError(errors, 'config', 'mappings', 'groupTypes', index);
        return (
          <MappingRow key={`${type?.apiName}-${index}`}>
            <StyledInputGroup title='Client Header' error={itemError}>
              <Input
                value={group.columnName ?? ''}
                onChange={(e) => setValue(`config.mappings.groupTypes[${index}].columnName`, e.target.value)}
                disabled={disabled || !type}
              />
            </StyledInputGroup>
            <IconContainer>
              <RightArrowIcon />
            </IconContainer>
            <StyledInputGroup title='Category'>
              <Select<GroupTypeType>
                options={availableOptions}
                value={selectedType}
                onChange={(v) => setValue(`config.mappings.groupTypes[${index}].type`, v)}
                getOptionValue={getApiName}
                getOptionLabel={getDisplayName}
                placeholder='Category'
                disabled={disabled}
              />
            </StyledInputGroup>
            <IconContainer width='36px'>
              <StyledDetailsIcon onClick={() => onOpenSlideout(LookupName.GroupTypes)} />
            </IconContainer>
            <IconContainer width='36px'>
              {!disabled && <StyledTrashIcon onClick={() => removeMapping(index)} />}
            </IconContainer>
          </MappingRow>
        );
      })}
      {!disabled && availableOptions.length > 0 && (
        <Link onClick={addMapping}>Add {groupTypes.length > 0 ? 'another' : 'a'} group type mapping</Link>
      )}
    </>
  );
}

function GroupMappings({
  values,
  setValue,
  disabled,
}: {
  values: any;
  setValue: (key: string, value: any) => void;
  disabled: boolean;
}) {
  const config = values.config;
  const groups = config.mappings.groups ?? [];

  const mappingGroupTypeOptions = config.mappings.groupTypes.map((mapping: any) => {
    const match = GROUP_TYPE_TYPE_OPTIONS.find((o) => mapping.type?.apiName.endsWith(o.apiName));
    return new GroupType({
      id: '',
      apiName: `mappings:group_types:${mapping.type?.apiName}`,
      displayName: '',
      name: `Mapping: ${mapping.columnName}`,
      isInpatient: false,
      config: {},
      createdAt: null,
      updatedAt: null,
      type: match
        ? { apiName: match.apiName as GroupTypeApiName, displayName: match.displayName }
        : { apiName: GroupTypeApiName.PROVIDER, displayName: '' },
    });
  });

  const groupTypeSelectOptions = useInfiniteOptions({
    queryKey: ['adminGroupTypes', 'groupMappings'],
    fetchFn: indexGroupTypes,
    params: { 'apiName.not': 'physician_team', sortBy: 'displayName asc' },
  });

  const getGroupTypeLabel = (gt: GroupType<GroupTypeFields>) => {
    if (gt.apiName?.startsWith('mappings:')) {
      const matched = mappingGroupTypeOptions.find((o: GroupType) => o.apiName === gt.apiName);
      if (matched) return `${matched.name} (${matched.type?.displayName ?? ''})`;
    }
    return `${gt.name} (${gt.type?.displayName ?? ''})`;
  };

  const addMapping = () =>
    setValue('config.mappings.groups', [...groups, { defaultValue: null, type: null, columnName: '' }]);
  const removeMapping = (i: number) =>
    setValue(
      'config.mappings.groups',
      groups.filter((_: any, idx: number) => idx !== i)
    );

  return (
    <>
      <SectionHeader>Group Mappings</SectionHeader>
      {groups.map((group: any, index: number) => {
        const { type } = group;
        return (
          <MappingRow key={`${type?.apiName}-${index}`}>
            <StyledInputGroup title='Group Type'>
              <Select<GroupType>
                {...groupTypeSelectOptions}
                prependedOptions={mappingGroupTypeOptions}
                value={type}
                onChange={(v) => setValue(`config.mappings.groups[${index}].type`, v)}
                getOptionValue={getApiName}
                getOptionLabel={getGroupTypeLabel}
                placeholder='Group Type'
                disabled={disabled}
              />
            </StyledInputGroup>
            <IconContainer width='36px'>
              {!disabled && <StyledTrashIcon onClick={() => removeMapping(index)} />}
            </IconContainer>
          </MappingRow>
        );
      })}
      {!disabled && <Link onClick={addMapping}>Add {groups.length > 0 ? 'another' : 'a'} group mapping</Link>}
    </>
  );
}

function LocationMappings({
  values,
  setValue,
  disabled,
  onOpenSlideout,
}: {
  values: any;
  setValue: (key: string, value: any) => void;
  disabled: boolean;
  onOpenSlideout: (name: LookupName) => void;
}) {
  const locations = values.config.mappings.locations ?? [{ columnName: '', type: null, defaultValue: null }];
  const mapping = locations[0] ?? { columnName: '', type: null, defaultValue: null };

  const locationOptions = useInfiniteOptions({
    queryKey: ['adminLocations', 'locationMappings', values.client?.id],
    fetchFn: adminIndexLocations,
    params: { clientId: values.client?.id, sortBy: 'name asc', archived: false },
  });

  return (
    <MappingRowWithSubRow>
      <MappingRow>
        <StyledInputGroup title='Client Header (optional)'>
          <Input
            value={mapping.columnName ?? ''}
            onChange={(e) => setValue('config.mappings.locations[0].columnName', e.target.value)}
            disabled={disabled}
          />
        </StyledInputGroup>
        <IconContainer>
          <RightArrowIcon />
        </IconContainer>
        <StyledInputGroup title='Location'>
          <Input value='Location' disabled />
        </StyledInputGroup>
        <IconContainer width='36px'>
          <StyledDetailsIcon onClick={() => onOpenSlideout(LookupName.Locations)} />
        </IconContainer>
      </MappingRow>
      <MappingRow>
        <StyledFormIndent />
        <InputGroup title='Default Value (optional)'>
          <Select<Location>
            {...locationOptions}
            value={mapping.defaultValue ?? null}
            onChange={(v) => setValue('config.mappings.locations[0].defaultValue', v)}
            getOptionValue={getId}
            getOptionLabel={getName}
            disabled={disabled}
            isClearable
          />
        </InputGroup>
      </MappingRow>
    </MappingRowWithSubRow>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function EditImportConfiguration() {
  const { id } = useParams<{ id: string }>();
  const { profile } = useProfile();
  const isEditing = !!id;
  const canEdit = profile.has(PERMISSIONS.adminImportConfigurationEdit);
  const disabled = !canEdit;

  const { values, errors, valid, dirty, setValue, submit, loading, goBack } = useAdminForm({
    id,
    show: adminShowImportConfiguration,
    create: adminCreateImportConfiguration,
    update: adminUpdateImportConfiguration,
    include: ['client'] as const,
    queryKey: adminImportConfigurationsQueryKeys,
    schema: validationSchema,
    onCreateSuccess: {
      toast: 'Import Configuration successfully added!',
      navigate: '/admin/clients/import-configurations',
    },
    onUpdateSuccess: {
      toast: 'Import Configuration successfully updated!',
      navigate: '/admin/clients/import-configurations',
    },
    onError: { toast: 'Something went wrong.' },
    toFormValues: (data) => {
      const rawType = data?.type || IMPORTED_PATIENT;
      const typeOption = CONFIGURATION_TYPE_OPTS.find((opt) => opt.value === rawType) ?? CONFIGURATION_TYPE_OPTS[0];
      const platformRaw = data?.config?.platform;
      const platformValue = typeof platformRaw === 'object' ? platformRaw?.value : platformRaw;
      const normalizedPlatform = typeof platformValue === 'string' ? platformValue.trim().toLowerCase() : platformValue;
      const platformOption = PLATFORM_OPTS.find((opt) => opt.value === normalizedPlatform) ?? null;

      return {
        id: data?.id ?? '',
        client: data?.client ?? (null as { id: string; name: string } | null),
        type: typeOption,
        config: {
          ...defaultConfig,
          ...data?.config,
          dateFormat: data?.config?.dateFormat || '%m/%d/%Y',
          delimiter:
            DELIMITER_OPTS.find(
              (opt) =>
                opt.value ===
                (typeof data?.config?.delimiter === 'object' ? data?.config?.delimiter?.value : data?.config?.delimiter)
            ) ?? DELIMITER_OPTS[0],
          platform: platformOption,
          lookups: { ...defaultConfig.lookups, ...data?.config?.lookups },
          mappings: {
            ...defaultConfig.mappings,
            ...data?.config?.mappings,
            groupTypes: data?.config?.mappings?.groupTypes || [],
            locations: data?.config?.mappings?.locations?.length
              ? data.config.mappings.locations
              : [{ columnName: '', type: null, defaultValue: null }],
          },
        },
      };
    },
    toPayload: (v) => {
      const ic = new ImportConfiguration({
        id: v.id,
        clientId: v.client?.id ?? '',
        type: v.type?.value ?? IMPORTED_PATIENT,
        updatedAt: '',
        config: v.config,
      } as ImportConfigurationOptions);
      return ic.serialize();
    },
  });

  const isIntegration = values.type?.value === INTEGRATION;
  const isFile = values.type?.value === IMPORTED_PATIENT;

  const handleTypeChange = (next: (typeof CONFIGURATION_TYPE_OPTS)[number]) => {
    const nextType = next?.value;
    if (nextType === values.type?.value) {
      setValue('type', next);
      return;
    }

    setValue('type', next);
    const { classifications, groups, groupTypes } = values.config.mappings;
    if (nextType === INTEGRATION) {
      setValue('config', {
        ...values.config,
        dateFormat: undefined,
        delimiter: undefined,
        platform: null,
        mappings: { classifications, groups, groupTypes },
      });
    } else {
      setValue('config', {
        ...values.config,
        platform: undefined,
        dateFormat: '%m/%d/%Y',
        delimiter: DELIMITER_OPTS[0],
        mappings: {
          ...values.config.mappings,
          transferredFrom: { columnName: '', type: null, defaultValue: null },
        },
      });
    }
  };

  const config = values.config;

  // Dropdown options
  const clientOptions = useInfiniteOptions({
    queryKey: ['adminClients', 'importConfig'],
    fetchFn: adminIndexClients,
    params: { sortBy: 'name asc', hasImportConfiguration: false },
  });

  const [hospitalGroupType, setHospitalGroupType] = useState<GroupType | null>(null);
  useEffect(() => {
    indexGroupTypes({ apiName: 'hospital' }).then((res) => {
      if (res.data.length > 0) setHospitalGroupType(res.data[0]);
    });
  }, []);

  // Slideout state
  const [selectedLookupName, setSelectedLookupName] = useState<LookupName | null>(null);
  const [showSlideout, setShowSlideout] = useState(false);

  const handleOpenSlideout = (lookupName: LookupName) => {
    setSelectedLookupName(lookupName);
    setShowSlideout(true);
  };

  if (isEditing && loading) {
    return (
      <AdminFormPage>
        <CircularProgress />
      </AdminFormPage>
    );
  }

  return (
    <AdminFormPage>
      <Form>
        <FormHeader title={isEditing ? 'Edit Import Configuration' : 'Add Import Configuration'} />

        <FormSection>
          <InputGroup title='Client' error={getFieldError(errors, 'client')}>
            <Select<Client>
              {...clientOptions}
              name='client'
              value={(values.client as Client) ?? null}
              onChange={(v) => setValue('client', v)}
              getOptionValue={getId}
              getOptionLabel={getName}
              placeholder='Client'
              disabled={disabled || isEditing}
              hasError={!!getFieldError(errors, 'client')}
            />
          </InputGroup>
          <InputGroup title='Configuration Type' error={getFieldError(errors, 'type')}>
            <Select
              options={CONFIGURATION_TYPE_OPTS}
              name='type'
              value={values.type ?? null}
              onChange={handleTypeChange}
              getOptionValue={(o) => o.value}
              getOptionLabel={(o) => o.label}
              placeholder='Configuration Type'
              disabled={disabled}
              hasError={!!getFieldError(errors, 'type')}
            />
          </InputGroup>
          {isIntegration && (
            <InputGroup title='System' error={getFieldError(errors, 'config', 'platform')}>
              <Select
                options={PLATFORM_OPTS}
                name='platform'
                value={config.platform ?? null}
                onChange={(v) => setValue('config.platform', v)}
                getOptionValue={(o) => o.value}
                getOptionLabel={(o) => o.label}
                placeholder='System'
                disabled={disabled}
                hasError={!!getFieldError(errors, 'config', 'platform')}
              />
            </InputGroup>
          )}
        </FormSection>

        {isFile && (
          <FormSection>
            <SectionHeader>File Format</SectionHeader>
            <InputGroup title='Delimiter' error={getFieldError(errors, 'config', 'delimiter')}>
              <Select
                options={DELIMITER_OPTS}
                name='delimiter'
                value={config.delimiter}
                onChange={(v) => setValue('config.delimiter', v)}
                getOptionValue={(o) => o.value}
                getOptionLabel={(o) => o.label}
                placeholder='Delimiter'
                disabled={disabled}
                hasError={!!getFieldError(errors, 'config', 'delimiter')}
              />
            </InputGroup>
            <InputGroup title='Date Format' error={getFieldError(errors, 'config', 'dateFormat')}>
              <Input
                value={config.dateFormat ?? ''}
                onChange={(e) => setValue('config.dateFormat', e.target.value)}
                placeholder='Date Format'
                disabled={disabled}
                $hasError={!!getFieldError(errors, 'config', 'dateFormat')}
              />
            </InputGroup>
          </FormSection>
        )}

        <FormSection>
          {isFile && (
            <>
              <SectionHeader>Patient Demographic Mappings</SectionHeader>
              {PATIENT_MAPPINGS.map(({ key, label, optional }) => (
                <MappingRow key={key}>
                  <StyledInputGroup title={`Client Header ${optional ? '(optional)' : ''}`}>
                    <Input
                      value={config.mappings[key] ?? ''}
                      onChange={(e) => setValue(`config.mappings.${key}`, e.target.value)}
                      disabled={disabled}
                    />
                  </StyledInputGroup>
                  <IconContainer>
                    <RightArrowIcon />
                  </IconContainer>
                  <StyledInputGroup title='Olio Field'>
                    <Input value={label} readOnly />
                  </StyledInputGroup>
                  <Spacer />
                </MappingRow>
              ))}
              <MappingRow>
                <StyledInputGroup title='Client Header (optional)'>
                  <Input
                    value={config.mappings.transferredFrom?.columnName ?? ''}
                    onChange={(e) => setValue('config.mappings.transferredFrom.columnName', e.target.value)}
                    disabled={disabled}
                  />
                </StyledInputGroup>
                <IconContainer>
                  <RightArrowIcon />
                </IconContainer>
                <StyledInputGroup title='Transferred From'>
                  <Select<GroupType>
                    options={hospitalGroupType ? [hospitalGroupType] : []}
                    value={hospitalGroupType}
                    onChange={() => {}}
                    getOptionValue={getId}
                    getOptionLabel={getName}
                    disabled
                  />
                </StyledInputGroup>
                <IconContainer width='36px'>
                  <DetailsIcon
                    style={{ color: colors.black50, cursor: 'pointer', width: 16 }}
                    onClick={() => handleOpenSlideout(LookupName.Groups)}
                  />
                </IconContainer>
              </MappingRow>
              <LocationMappings
                values={values}
                setValue={setValue}
                disabled={disabled}
                onOpenSlideout={handleOpenSlideout}
              />
            </>
          )}

          <ClassificationMappings
            values={values}
            errors={errors}
            setValue={setValue}
            disabled={disabled}
            onOpenSlideout={handleOpenSlideout}
          />
          <GroupTypeMappings
            values={values}
            errors={errors}
            setValue={setValue}
            disabled={disabled}
            onOpenSlideout={handleOpenSlideout}
          />
          {isFile && <GroupMappings values={values} setValue={setValue} disabled={disabled} />}
        </FormSection>

        <ButtonGroup>
          <Button variant='ghost' onClick={goBack}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={disabled || !dirty || !valid}>
            Submit
          </Button>
        </ButtonGroup>
      </Form>

      <Foldout
        open={showSlideout}
        position='right'
        width='min(80%, 917px)'
        onBackdropClick={() => setShowSlideout(false)}>
        {selectedLookupName && (
          <LookupsSlideout
            lookupName={selectedLookupName}
            lookups={config.lookups[selectedLookupName] || {}}
            handleClose={() => setShowSlideout(false)}
            handleApply={(_lookupName, lookups) => {
              if (selectedLookupName) setValue(`config.lookups.${selectedLookupName}`, lookups);
              setShowSlideout(false);
            }}
            disabled={disabled}
          />
        )}
      </Foldout>
    </AdminFormPage>
  );
}

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const IconContainer = styled.div<{ width?: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${(props) => props.width || '60px'};
`;

const Link = styled.a`
  color: ${colors.blue100};
  cursor: pointer;
  display: flex;
  margin-bottom: 24px;
  font-size: 14px;
`;

const MappingRow = styled.div`
  display: flex;
  flex-direction: row;
`;

const MappingRowWithSubRow = styled(MappingRow)`
  flex-direction: column;
  & > :last-child {
    align-self: flex-end;
    margin-top: -16px;
    width: 40%;
  }
`;

const StyledInputGroup = styled(InputGroup)`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const StyledFormIndent = styled(FormIndent)`
  margin-right: 8px;
`;

const StyledDetailsIcon = styled(DetailsIcon)`
  color: ${colors.black50};
  cursor: pointer;
  width: 16px;
`;

const StyledTrashIcon = styled(TrashIcon)`
  color: ${colors.red100};
  cursor: pointer;
`;

const Spacer = styled.div`
  width: 36px;
  flex-shrink: 0;
`;
